import React, { useId } from "react";
import {
  DesignDialogButton,
  DesignDialogGlyph,
  renderHighlightedMessage,
} from "./DesignDialog";
import { DesignModalShell, designModalMessageStyle } from "./DesignModalShell";

export type ActionSuccessDialogAction = {
  key: string;
  label: string;
  onClick: () => void;
};

export type ActionSuccessDialogProps = {
  /** The dialog is open while a message is present. */
  message: string | null | undefined;
  /** Substrings of the message rendered in bold, like the design. */
  highlights?: readonly string[];
  note?: React.ReactNode;
  /** Gold follow-up actions shown under the message. */
  actions?: readonly ActionSuccessDialogAction[];
  /** Red icon for a failed action, as the design's error variant. */
  tone?: "success" | "error";
  onClose: () => void;
};

// The Claude Design ActionSuccessModal: a message-only pop-up with a 64px
// green check (or red warning), bold highlights, and optional gold actions.
// Use it for every completed action instead of toasts or inline banners.
export const ActionSuccessDialog = ({
  message,
  highlights,
  note,
  actions,
  tone = "success",
  onClose,
}: ActionSuccessDialogProps) => {
  const messageId = useId();
  const isError = tone === "error";
  return (
    <DesignModalShell
      open={Boolean(message)}
      onClose={onClose}
      circleColor={isError ? "#F21B1B" : "#12B981"}
      icon={<DesignDialogGlyph icon={isError ? "warning" : "success"} />}
      describedBy={message ? messageId : undefined}
      ariaLabel={message || (isError ? "Action failed" : "Action completed")}
    >
      <div
        id={messageId}
        className="[&_b]:font-semibold [&_strong]:font-semibold"
        style={{ ...designModalMessageStyle, marginTop: 18 }}
      >
        {renderHighlightedMessage(message || "", highlights)}
      </div>
      {note ? (
        <div style={{ ...designModalMessageStyle, marginTop: 10 }}>{note}</div>
      ) : null}
      {actions && actions.length > 0 ? (
        <div className="mt-[22px] flex w-full flex-wrap justify-center gap-[12px] [&>button]:max-w-[170px]">
          {actions.map((action) => (
            <DesignDialogButton key={action.key} tone="gold" onClick={action.onClick}>
              {action.label}
            </DesignDialogButton>
          ))}
        </div>
      ) : null}
    </DesignModalShell>
  );
};

export default ActionSuccessDialog;
